import { useMemo } from "react";
import { Authentication } from "./useQueryAuthentications";
import {
  Implementation,
  useQueryImplementations,
} from "../implementation/useQueryImplementations";

interface Input {
  authentications: Authentication[];
  enabled?: boolean;
}

/** This hook takes an array of authentications and hydrates them with
 * an implementation object from the `/api/v4/implementations endpont.
 * This object is used for rendering app implementation details like names and images.
 */

export function useHydrateAuthenticationsWithImplementations({
  authentications,
  enabled = true,
}: Input) {
  // Build a unique list of selected APIs from the authentications
  const selectedApis = useMemo(() => {
    const auths = authentications.reduce(
      (accumulator, auth) => {
        if (!auth.selectedApi) {
          return accumulator;
        }
        const key = auth.selectedApi.split("@")[0];
        accumulator[key] = true;
        return accumulator;
      },
      {} as Record<string, boolean>,
    );
    return Object.keys(auths);
  }, [authentications]);

  // Query for implementations for the selected APIs
  const { implementations, isLoading } = useQueryImplementations(
    {
      selectedApis,
    },
    {
      enabled: authentications.length > 0 && enabled,
    },
  );

  // Build a hash of the implementations by selected API
  const implementationsHash = useMemo(() => {
    return implementations.reduce(
      (accumulator, imp) => {
        if (!imp.selectedApi) {
          return accumulator;
        }
        const key = imp.selectedApi.split("@")[0];
        accumulator[key] = imp;
        return accumulator;
      },
      {} as Record<string, Implementation>,
    );
  }, [implementations]);

  // Hydrate authentications with implementation objects
  const hydratedAuths = authentications.map((auth) => {
    const key = auth.selectedApi ? auth.selectedApi.split("@")[0] : undefined;
    return {
      ...auth,
      implementation: key ? implementationsHash[key] : undefined,
    };
  });

  if (!enabled) {
    return {
      authentications: authentications,
      isLoading: false,
    };
  }

  return {
    authentications: hydratedAuths,
    isLoading,
  };
}
